import { Close } from "@mui/icons-material";
import { Button, IconButton, Snackbar } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { orthoCamActions } from "features/orthoCam";
import { pointLineActions, selectCurrentPointLine, selectDraggingPoint } from "features/pointLine";
import { Picker, renderActions, selectPicker } from "features/render";
import { hasMouseSupport } from "utils/misc";

const localStorageKey = "pickerEscSnackbarDismissed";

const pickerTypes = [
    Picker.Area,
    Picker.ClippingPlane,
    Picker.Measurement,
    Picker.OutlineLaser,
    Picker.PointLine,
    Picker.Manhole,
    Picker.CrossSection,
    Picker.JiraIssuePosition,
    Picker.Coordinates,
    Picker.Dimension,
    Picker.FollowPathObject,
    Picker.SectionPlane,
];

export function PickerEscSnackbar() {
    const { t } = useTranslation();
    const picker = useAppSelector(selectPicker);
    const pickerRef = useRef(picker);
    pickerRef.current = picker;
    const [open, setOpen] = useState(false);
    const dispatch = useAppDispatch();
    const hasMouse = hasMouseSupport();
    const currentPointLine = useAppSelector(selectCurrentPointLine);
    const isDraggingPoint = Boolean(useAppSelector(selectDraggingPoint));

    useEffect(() => {
        if (!hasMouse) {
            return;
        }

        const dismissed = localStorage.getItem(localStorageKey) === "true";
        if (dismissed) {
            return;
        }

        if (pickerTypes.includes(picker)) {
            setOpen(true);
        }
    }, [picker, hasMouse]);

    useEffect(() => {
        function onKeyDown(e: KeyboardEvent) {
            if (e.key === "Escape") {
                if (pickerRef.current === Picker.PointLine) {
                    if (isDraggingPoint) {
                        dispatch(pointLineActions.setDraggingPoint(null));
                    } else if (currentPointLine.points.length > 0) {
                        dispatch(pointLineActions.newPointLine());
                    } else {
                        dispatch(renderActions.setPicker(Picker.Object));
                    }
                } else if (pickerTypes.includes(pickerRef.current)) {
                    dispatch(renderActions.setPicker(Picker.Object));
                    dispatch(orthoCamActions.setCrossSectionPoint(undefined));
                }
            }
        }

        window.addEventListener("keydown", onKeyDown);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [dispatch, currentPointLine.points.length, isDraggingPoint]);

    const handleClose = () => {
        setOpen(false);
    };

    const dismiss = () => {
        setOpen(false);
        localStorage.setItem(localStorageKey, "true");
    };

    return (
        <>
            <Snackbar
                open={open}
                autoHideDuration={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                sx={{
                    width: { xs: "auto", sm: 350 },
                    bottom: { xs: "auto", sm: 88 },
                    top: { xs: 24, sm: "auto" },
                    pointerEvents: "auto",
                }}
                onClose={handleClose}
                message={
                    <>
                        {t("press") + " "}
                        <kbd>Escape</kbd>
                        {" " + t("toExitCurrentSelectionMode")}.
                    </>
                }
                action={
                    <>
                        <Button color="primary" size="small" variant="text" type="button" onClick={dismiss}>
                            {t("doNotShowAgainUppercase")}
                        </Button>
                        <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
                            <Close fontSize="small" />
                        </IconButton>
                    </>
                }
            />
        </>
    );
}
